import { getToken } from "@/lib/clerk";

export type DenyReason =
  | "unauthenticated"
  | "forbidden_domain"
  | "deactivated"
  | "not_registered"
  | "no_email"
  | "forbidden";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly reason: DenyReason | null = null,
  ) {
    super(message);
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * One fetch wrapper for the whole SPA. Same origin (`/api`), JSON in and out,
 * the Bearer token attached when a session exists, and every non-2xx turned
 * into an ApiError that carries the server's `reason` where it gave one.
 */
export async function api<T>(
  path: string,
  init: Omit<RequestInit, "body"> & { body?: unknown; raw?: boolean } = {},
): Promise<T> {
  const { body, raw, headers, ...rest } = init;
  const isForm = body instanceof FormData;
  const res = await fetch(`/api${path}`, {
    ...rest,
    headers: {
      ...(await authHeaders()),
      ...(body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
      ...(headers as Record<string, string> | undefined),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });

  if (!res.ok) {
    let message = res.statusText || `Request failed (${res.status})`;
    let reason: DenyReason | null = null;
    try {
      const data = (await res.json()) as { error?: string; reason?: DenyReason };
      if (typeof data.error === "string") message = data.error;
      if (data.reason) reason = data.reason;
    } catch {
      /* no JSON body */
    }
    throw new ApiError(res.status, message, reason);
  }

  if (raw) return (await res.blob()) as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Same-origin download that still carries the Bearer token. */
export async function download(path: string, filename: string): Promise<void> {
  const blob = await api<Blob>(path, { raw: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
